// Badge / StatusPill — pale-tinted pill for tag categorization.
//
// Variants per spec §3.4.5 and tokens/colors.ts:
//   category    — pale Sage bg, Teal text  (Compliance, Onboarding, Renewal, ...)
//   regulatory  — pale Ember bg, Ember text (ACA, ERISA, COBRA, HIPAA)
//   neutral     — recessed bg, text-secondary (catch-all)
//
// Sizes:
//   sm — px-2 py-0.5 text-xs (default — matches Pencil tag pills)
//   md — px-3 py-1 text-sm (larger contexts)

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";

export type BadgeVariant = "category" | "regulatory" | "neutral";
export type BadgeSize = "sm" | "md";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<BadgeVariant, string> = {
  category: "bg-sage-tint text-teal",
  regulatory: "bg-ember-tint text-ember-ink",
  neutral: "bg-recessed text-text-secondary",
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  sm: "px-2 py-0.5 text-xs",
  md: "px-3 py-1 text-sm",
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = "category", size = "sm", className, children, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      data-variant={variant}
      data-size={size}
      className={cn(
        "inline-flex items-center rounded-sm font-medium whitespace-nowrap",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
});
